import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import { useEffect } from "react";
import { createAnalysis } from "@/lib/api";
import { listWorkers } from "@/lib/workers";
import { UploadSimple, FilmStrip, ArrowRight, X, CheckCircle, Warning, Spinner } from "@phosphor-icons/react";

const INDUSTRIES = ["Manufacturing","Warehouse & Logistics","Construction","Healthcare","Retail & Hospitality","Agriculture","Office / Computer Work","Other"];
const TASK_TYPES = ["Lifting / Lowering","Pushing / Pulling","Carrying","Reaching Overhead","Bending / Stooping","Twisting","Repetitive Hand Motion","Static Posture","Patient Handling","Tool Operation","Other"];

export default function BulkUpload() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  // Shared metadata applied to every clip
  const [industry, setIndustry] = useState(INDUSTRIES[0]);
  const [taskType, setTaskType] = useState(TASK_TYPES[0]);
  const [duration, setDuration] = useState(120);
  const [workerId, setWorkerId] = useState("");
  const [workers, setWorkers] = useState([]);
  const [blurFaces, setBlurFaces] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { (async () => { try { setWorkers(await listWorkers()); } catch (e) {} })(); }, []);

  const onSelect = (list) => {
    const arr = Array.from(list || []);
    const accepted = [];
    for (const f of arr) {
      if (!f.type.startsWith("video/")) { toast.error(`Skipped non-video: ${f.name}`); continue; }
      if (f.size > 120 * 1024 * 1024) { toast.error(`Too large: ${f.name}`); continue; }
      accepted.push({ id: `${f.name}-${f.size}-${f.lastModified}`, file: f, title: f.name.replace(/\.[^.]+$/, ""), progress: 0, status: "pending" });
    }
    setFiles((curr) => [...curr, ...accepted]);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    onSelect(e.dataTransfer.files);
  };

  const remove = (id) => setFiles((curr) => curr.filter((f) => f.id !== id));
  const updateTitle = (id, t) => setFiles((curr) => curr.map((f) => (f.id === id ? { ...f, title: t } : f)));

  const submit = async () => {
    if (files.length === 0) { toast.error("Add at least one video"); return; }
    setSubmitting(true);
    let okCount = 0;
    for (const item of files) {
      if (item.status === "done") { okCount++; continue; }
      setFiles((curr) => curr.map((f) => (f.id === item.id ? { ...f, status: "uploading", progress: 0 } : f)));
      const form = new FormData();
      form.append("file", item.file);
      form.append("title", item.title || item.file.name);
      form.append("industry", industry);
      form.append("task_type", taskType);
      form.append("duration_minutes", String(duration));
      if (workerId) form.append("worker_id", workerId);
      form.append("blur_faces", String(blurFaces));
      try {
        await createAnalysis(form, (p) => {
          setFiles((curr) => curr.map((f) => (f.id === item.id ? { ...f, progress: p } : f)));
        });
        setFiles((curr) => curr.map((f) => (f.id === item.id ? { ...f, status: "done", progress: 100 } : f)));
        okCount++;
      } catch (e) {
        setFiles((curr) => curr.map((f) => (f.id === item.id ? { ...f, status: "failed", error: e?.response?.data?.detail || "Upload failed" } : f)));
      }
    }
    setSubmitting(false);
    if (okCount > 0) {
      toast.success(`${okCount} of ${files.length} clip${files.length === 1 ? "" : "s"} queued for analysis`);
      setTimeout(() => nav("/dashboard"), 1200);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="app" />
      <Toaster richColors position="top-right" />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-2">Bulk analysis</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-ink-900">Upload multiple clips.</h1>
        <p className="mt-3 text-ink-700 max-w-2xl">Drop a folder's worth of clips for a full-line audit. Shared metadata is applied to every clip. Each gets its own analysis you can review independently.</p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Drop zone + list */}
          <div className="lg:col-span-2 space-y-4">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`border ${dragOver ? "border-safety bg-safety/5" : "border-ink-100"} transition-colors`}
              data-testid="bulk-dropzone"
            >
              <input ref={fileRef} type="file" accept="video/*" multiple className="hidden" data-testid="bulk-file-input" onChange={(e) => { onSelect(e.target.files); e.target.value = ""; }} />
              <div className="p-12 text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-ink-900 mb-4">
                  <UploadSimple size={22} weight="bold" className="text-white" />
                </div>
                <div className="font-display font-bold text-2xl tracking-tight text-ink-900">Drop multiple videos here</div>
                <div className="mt-2 text-ink-500">or click to browse · mp4, mov, webm · up to 120 MB each</div>
                <Button onClick={() => fileRef.current?.click()} size="lg" className="mt-6 rounded-none bg-ink-900 hover:bg-safety text-white h-12 px-6" data-testid="bulk-browse">
                  <UploadSimple size={16} weight="bold" className="mr-2" /> Add files
                </Button>
              </div>
            </div>

            {files.length > 0 && (
              <div className="border border-ink-100 divide-y divide-ink-100">
                <div className="p-3 bg-ink-50 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-700">
                  Queue · {files.length} clip{files.length === 1 ? "" : "s"}
                </div>
                {files.map((f) => (
                  <div key={f.id} className="p-4" data-testid={`bulk-item-${f.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-ink-900 flex items-center justify-center shrink-0"><FilmStrip size={14} weight="bold" className="text-white" /></div>
                      <div className="flex-1 min-w-0">
                        <Input
                          value={f.title}
                          onChange={(e) => updateTitle(f.id, e.target.value)}
                          disabled={submitting}
                          className="rounded-none border-ink-100 h-9 focus-visible:ring-safety"
                          data-testid={`bulk-title-${f.id}`}
                        />
                        <div className="mt-1 flex items-center justify-between text-xs font-mono text-ink-500">
                          <span className="truncate">{f.file.name}</span>
                          <span className="shrink-0">{(f.file.size / (1024 * 1024)).toFixed(1)} MB</span>
                        </div>
                        {f.status !== "pending" && (
                          <div className="mt-2 h-1 bg-ink-100">
                            <div className={`h-full ${f.status === "failed" ? "bg-hazard-critical" : f.status === "done" ? "bg-hazard-safe" : "bg-safety"}`} style={{ width: `${f.progress}%` }} />
                          </div>
                        )}
                        {f.status === "failed" && <div className="mt-1 text-xs text-hazard-critical">{f.error}</div>}
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        {f.status === "done" && <CheckCircle size={16} weight="fill" className="text-hazard-safe" />}
                        {f.status === "uploading" && <Spinner size={16} weight="bold" className="text-safety animate-spin" />}
                        {f.status === "failed" && <Warning size={16} weight="bold" className="text-hazard-critical" />}
                        {!submitting && f.status !== "done" && (
                          <button onClick={() => remove(f.id)} className="text-ink-500 hover:text-hazard-critical" aria-label="Remove" data-testid={`bulk-remove-${f.id}`}>
                            <X size={14} weight="bold" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Shared metadata */}
          <div className="lg:col-span-1 border border-ink-100 p-6 space-y-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Apply to all clips</div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Industry</Label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger data-testid="bulk-industry" className="mt-2 rounded-none border-ink-100"><SelectValue /></SelectTrigger>
                <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Task type</Label>
              <Select value={taskType} onValueChange={setTaskType}>
                <SelectTrigger data-testid="bulk-task-type" className="mt-2 rounded-none border-ink-100"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Duration / shift (min)</Label>
              <Input data-testid="bulk-duration" type="number" min={1} max={720} value={duration} onChange={(e) => setDuration(Number(e.target.value || 0))} className="mt-2 rounded-none border-ink-100" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Worker (optional)</Label>
              <Select value={workerId || "__none__"} onValueChange={(v) => setWorkerId(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="bulk-worker" className="mt-2 rounded-none border-ink-100"><SelectValue placeholder="No worker" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No worker</SelectItem>
                  {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-start gap-3 border border-ink-100 p-3 cursor-pointer hover:bg-ink-50 transition-colors" data-testid="bulk-blur-faces-row">
              <input
                type="checkbox"
                checked={blurFaces}
                onChange={(e) => setBlurFaces(e.target.checked)}
                className="mt-1 h-4 w-4 accent-safety cursor-pointer"
                data-testid="bulk-blur-faces-toggle"
              />
              <div className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-900">Blur faces (recommended)</div>
                <div className="text-xs text-ink-700 mt-1 leading-relaxed">Anonymize every detected face before storage or AI analysis.</div>
              </div>
            </label>
            <Button
              data-testid="bulk-submit"
              onClick={submit}
              disabled={submitting || files.length === 0}
              size="lg"
              className="w-full rounded-none bg-safety hover:bg-safety-hover text-white h-12 text-sm font-semibold disabled:opacity-50"
            >
              {submitting ? "Uploading…" : (<>Analyze {files.length || ""} clip{files.length === 1 ? "" : "s"} <ArrowRight size={14} weight="bold" className="ml-2" /></>)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
